import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Steps.css";
import { useLocation, useNavigate } from "react-router-dom";
import { TextField, Button } from '@mui/material';
import { useForwardChevron } from '../../../i18n/DirectionalIcons';
import axios from "axios";
import ExtraFields from "../ExtraFields";
import { fetchSiteConfig } from "../../../redux/actions/dataActions";
import { useT } from "../../../i18n";

function isValidIsraeliIdNumber(id) {
  // Remove any extra spaces
  let trimmed = id.trim();

  // Check that the string is numeric and exactly 9 digits (do not allow padding)
  if (!/^\d+$/.test(trimmed) || trimmed.length !== 9) {
    return false;
  }

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let digit = parseInt(trimmed[i], 10);
    // Multiply with alternating factors: 1 for even index and 2 for odd index
    let multiplier = (i % 2) + 1;
    let product = digit * multiplier;

    // If product is greater than 9, subtract 9 (equivalent to adding its digits)
    if (product > 9) {
      product = product - 9;
    }
    sum += product;
  }

  return sum % 10 === 0;
}


function Step1(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useT();
  const FwdChevron = useForwardChevron();

  const [id_number, set_id_number] = useState(props.formData.id_number || "");
  const [fname, set_fname] = useState(props.formData.fname || "");
  const [lname, set_lname] = useState(props.formData.lname || "");
  /* ★★ F1 — שגיאות נשמרות כ**דגל/מפתח** ולא כטקסט מתורגם.
     ⚠️ אם היינו שומרים את המחרוזת עצמה, החלפת-שפה בזמן ששגיאה מוצגת הייתה
        משאירה על המסך את הטקסט בשפה הקודמת עד לפעולה הבאה של האזרח. */
  const [idError, setIdError] = useState(false);

  /* C2b — השדות הנוספים שהלקוח הגדיר בניהול. נטענים מ-`site-config`, ולכן
     לקוח שלא הגדיר דבר מקבל מערך ריק והטופס נשאר בדיוק כפי שהיה.
     ⚠️ כישלון-רשת מחזיר {} מ-fetchSiteConfig ⇒ הטופס עדיין עובד ואפשר
     לקבוע תור; שדה נוסף לא יחסום אזרח בגלל תקלה בהגדרות. */
  const [xDefs, setXDefs] = useState([]);
  /* ★★ C2a — שדה כתובת-מייל מובנה, נפרד מהשדות מוגדרי-הלקוח.
     מוצג רק כשהלקוח הפעיל אותו בהגדרות ("שדה כתובת מייל למוזמן" = האזרח /
     שניהם). הערך נשמר על **זהות-האזרח** ולא כשדה-תור, ולכן הוא אותו ערך
     בכל תור — וגם זה שישמש אם ידליקו קבלת-הודעות במייל.
     ★ אינו שדה-חובה: אזרח שלא ימלא אותו ממשיך בדיוק כמו היום. */
  const [emailField, setEmailField] = useState(false);
  const [email, setEmail] = useState(props.formData.email || "");
  const [xVals, setXVals] = useState(props.formData.extra_fields || {});
  const [xErrors, setXErrors] = useState({});

  useEffect(() => {
    let alive = true;
    fetchSiteConfig()(dispatch).then((cfg) => {
      if (alive && Array.isArray(cfg?.visitor_fields)) setXDefs(cfg.visitor_fields);
      if (alive && cfg?.email_field) setEmailField(true);
    });
    return () => { alive = false; };
  }, [dispatch]);

  const setXField = (key, value) => {
    setXVals((prev) => ({ ...prev, [key]: value }));
    // ניקוי השגיאה מיד עם התיקון, ולא רק בלחיצה הבאה על "שלב הבא"
    setXErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  // Allowed characters: English letters, Hebrew letters, spaces, and hyphen.
  const nameAllowedRegex = /^[a-zA-Z\u0590-\u05FF\s\-]*$/;

  // Handler for the ID field (numeric input only)
  const handleTzChange = (e) => {
    const newVal = e.target.value;
    if (/^\d*$/.test(newVal)) {
      set_id_number(newVal);
      if (newVal.length === 9) {
        setIdError(!isValidIsraeliIdNumber(newVal));
      } else {
        setIdError(false);
      }
    }
  };

  // Handler for the first name: allow only valid characters.
  const handleFnameChange = (e) => {
    const newVal = e.target.value;
    if (nameAllowedRegex.test(newVal)) {
      set_fname(newVal);
    }
  };

  // Handler for the last name: allow only valid characters.
  const handleLnameChange = (e) => {
    const newVal = e.target.value;
    if (nameAllowedRegex.test(newVal)) {
      set_lname(newVal);
    }
  };

  const Next = () => {
    if (!lname || !fname || !id_number) {
      return;
    }
    // Final validation for ID.
    if (!isValidIsraeliIdNumber(id_number)) {
      setIdError(true);
      return;
    }

    /* C2b — שדות-חובה שהלקוח הגדיר. הבדיקה כאן היא נוחות בלבד; השרת
       מאמת שוב ב-CreateAction, כי הפרונט אינו גבול-אבטחה. */
    const errs = {};
    xDefs.forEach((f) => {
      if (!f.required) return;
      const v = xVals[f.key];
      const empty = f.type === "checkbox" ? v !== "1" : !String(v ?? "").trim();
      if (empty) {
        // ★ מפתח-תרגום, לא טקסט — ראה ההסבר ב-`idError` למעלה.
        errs[f.key] = f.type === "checkbox" ? "step1.mustCheck" : "step1.requiredField";
      }
    });
    if (Object.keys(errs).length) {
      setXErrors(errs);
      return;
    }

    props.setFormData({
      ...props.formData,
      id_number: id_number,
      // trim leading/trailing spaces — mobile autocomplete + the keyboard "next" key
      // often append a trailing space, which would otherwise be saved into the name.
      fname: fname.trim(),
      lname: lname.trim(),
      extra_fields: xVals
    });
    props.next();
  };

  return (
    <div
      className="step step1"
      onKeyUp={(e) => {
        if (e.key === "Enter") {
          Next();
        }
      }}
    >
      <h3>{t("step1.title")}</h3>

      <TextField
        value={id_number}
        required
        label={t("step1.idLabel")}
        onChange={handleTzChange}
        placeholder={t("step1.idPlaceholder")}
        type="tel"
        name="id_number"
        autoComplete="on"
        className="input_tel"
        error={idError}
        helperText={idError ? t("step1.idInvalid") : ""}
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*"
        }}
      />

      <TextField
        required
        value={fname}
        label={t("step1.fnameLabel")}
        name="fname"
        autoComplete="given-name"
        onChange={handleFnameChange}
        placeholder={t("step1.fnamePlaceholder")}
      />

      <TextField
        required
        value={lname}
        label={t("step1.lnameLabel")}
        name="lname"
        autoComplete="family-name"
        onChange={handleLnameChange}
        placeholder={t("step1.lnamePlaceholder")}
      />

      {/* ★ C2a — שדה-המייל המובנה, לפני השדות מוגדרי-הלקוח: הוא חלק מפרטי
          הקשר של המוזמן (שם · ת״ז · נייד · מייל) ולא "שדה נוסף".
          ⚠️ `dir="ltr"` על הקלט בלבד — התווית נשארת בכיוון השפה. */}
      {emailField && (
        <TextField
          value={email}
          label={t("step1.emailLabel")}
          name="email"
          type="email"
          autoComplete="email"
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          inputProps={{ dir: "ltr", style: { direction: "ltr", textAlign: "left" } }}
        />
      )}

      {/* C2b — השדות שהלקוח הגדיר, מיד אחרי שם-המשפחה (בקשת-משתמש).
          ★ F1 — ‏`xErrors` מחזיק מפתחות; התרגום קורה **כאן**, בזמן הרינדור,
            כך שהחלפת-שפה מעדכנת גם שגיאות שכבר מוצגות. */}
      <ExtraFields
        defs={xDefs}
        values={xVals}
        errors={Object.fromEntries(
          Object.entries(xErrors).map(([k, v]) => [k, v ? t(v) : ""])
        )}
        onChange={setXField}
      />

      <Button
        variant="contained"
        disabled={!lname || !fname || !id_number}
        className="next-btn"
        onClick={Next}
        sx={{ borderRadius: 0, fontSize: 18 }}
        endIcon={<FwdChevron />}
      >
        {t("common.next")}
      </Button>
    </div>
  );
}

export default Step1;
